import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  Alert,
  Snackbar,
  Chip,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Grid,
  Divider,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  PlayArrow as PlayIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthWithCustomerOverride } from '../../hooks/useAuthWithCustomerOverride';
import api from '../../services/api';
import { useCustomerFilter } from '../../hooks/useCustomerFilter';
import { validateActionTarget } from '../../utils/pbxActionValidation';

interface RingGroup {
  id: number;
  group_number: string;
  group_name: string;
  ring_strategy: 'ringall' | 'sequential' | 'random' | 'roundrobin' | 'leastrecent' | 'fewestcalls';
  ring_time: number;
  ring_time_per_member: number;
  skip_busy: boolean;
  expand_followme: boolean;
  expand_sequential: boolean;
  expand_cell: boolean;
  announcement_id?: number;
  announcement_name?: string;
  max_wait_time: number;
  overflow_action?: number;
  overflow_action_name?: string;
  overflow_target?: string;
  overflow_target_desc?: string;
  overflow_requires_target?: boolean;
  member_count: number;
  active: boolean;
}

interface GroupMember {
  id: number;
  member_value: string;
  member_type: 'extension' | 'external';
  display_name?: string;
  priority: number;
  ring_timeout?: number | null;
  active: boolean | number;
}

interface Extension {
  extension: string;
  name: string;
  email: string;
}

const RingGroupsV2: React.FC = () => {
  const authOverride = useAuthWithCustomerOverride();
  const { member, isLoginAsCustomer } = authOverride;
  const customerFilter = useCustomerFilter();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Check for login token on initialization
  const urlParams = new URLSearchParams(window.location.search);
  const hasLoginToken = urlParams.has('lat');
  const hasSessionData = !!sessionStorage.getItem('loginAsCustomer');
  const [waitingForLoginAs] = useState(() => {
    const shouldWait = hasLoginToken && !hasSessionData;
    if (shouldWait) {
      console.log('[RingGroupsV2] Initializing with waiting=true due to login token');
    }
    return shouldWait;
  });
  
  // Main state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [ringGroups, setRingGroups] = useState<RingGroup[]>([]);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<RingGroup | null>(null);
  
  // Form data
  const [formData, setFormData] = useState({
    group_number: '',
    group_name: '',
    ring_strategy: 'ringall' as 'ringall' | 'sequential' | 'random' | 'roundrobin' | 'leastrecent' | 'fewestcalls',
    ring_time: 18,
    ring_time_per_member: 12,
    skip_busy: true,
    expand_cell: true,
    expand_followme: false,
    expand_sequential: false,
    announcement_id: '',
    max_wait_time: 180,
    overflow_action: 7,
    overflow_target: '1000',
  });
  
  // Members state - using a Map for better performance
  const [membersMap, setMembersMap] = useState<Map<number, GroupMember[]>>(new Map());
  const [availableExtensions, setAvailableExtensions] = useState<Extension[]>([]);
  const [memberActionLoading, setMemberActionLoading] = useState(false);
  const [externalNumber, setExternalNumber] = useState('');
  
  // Resources
  const [recordings, setRecordings] = useState<any[]>([]);
  const [overflowActions, setOverflowActions] = useState<any[]>([]);
  const [overflowTargets, setOverflowTargets] = useState<any[]>([]);
  const [loadingTargets, setLoadingTargets] = useState(false);

  // Get members for current group
  const currentGroupMembers = useMemo(() => {
    if (!selectedGroup) return [];
    return membersMap.get(selectedGroup.id) || [];
  }, [selectedGroup, membersMap]);

  // Fetch ring groups
  const fetchRingGroups = useCallback(async () => {
    // SAFETY CHECK: Don't fetch if waiting for login-as data
    if (waitingForLoginAs) {
      console.log('[RingGroupsV2] Still waiting for login-as data, aborting fetch');
      return;
    }
    
    // Verify session data matches member if in login-as mode
    const sessionData = sessionStorage.getItem('loginAsCustomer');
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        if (parsed.customerId && parsed.customerId !== member?.customerID) {
          console.error('[RingGroupsV2] CRITICAL: Customer ID mismatch!', {
            sessionCustomerId: parsed.customerId,
            memberCustomerId: member?.customerID
          });
          setError('Customer context mismatch - please refresh the page');
          setLoading(false);
          return;
        }
      } catch (e) {
        console.error('[RingGroupsV2] Error parsing session data:', e);
      }
    }
    
    try {
      setLoading(true);
      const params: any = {};
      
      if (isLoginAsCustomer && member?.customerID) {
        console.log('[RingGroupsV2] LOGIN AS MODE - Using customerID:', member.customerID);
        params.customerId = member.customerID;
      } else if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
        console.log('[RingGroupsV2] FILTER MODE - Using customerFilter:', customerFilter.customerId);
        params.customerId = customerFilter.customerId;
      } else {
        console.log('[RingGroupsV2] NO FILTER - Will use JWT customer');
      }
      
      const response = await api.get('/endpoints/ring_groups_v2', { params });
      if (response.data.success) {
        setRingGroups(response.data.data || []);
      }
    } catch (err: any) {
      setError('Failed to fetch ring groups');
    } finally {
      setLoading(false);
    }
  }, [customerFilter.isFiltered, customerFilter.customerId, member?.memberType, member?.customerID, isLoginAsCustomer, waitingForLoginAs]);

  // Fetch available data
  const fetchAvailableData = useCallback(async () => {
    try {
      const params: any = {};

      if (isLoginAsCustomer && member?.customerID) {
        params.customerId = member.customerID;
        console.log('[RingGroupsV2] Fetching recordings for login-as customer:', member.customerID);
      } else if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
        params.customerId = customerFilter.customerId;
        console.log('[RingGroupsV2] Fetching recordings for filtered customer:', customerFilter.customerId);
      } else {
        console.log('[RingGroupsV2] Fetching recordings for own customer');
      }

      // For ring groups, we need ALL extensions (not just unassigned)
      const extensionsParams = { ...params, includeAssigned: 'true' };

      const [extensionsRes, appsRes, recordingsRes] = await Promise.all([
        api.get('/endpoints/available_extensions', { params: extensionsParams }),
        api.get('/endpoints/overflow/applications'),
        api.get('/endpoints/recordings', { params }),
      ]);

      if (extensionsRes.data.success) {
        setAvailableExtensions(extensionsRes.data.data || []);
      }
      if (appsRes.data.success) {
        setOverflowActions(appsRes.data.data || []);
      }
      if (recordingsRes.data.success) {
        console.log('[RingGroupsV2] Recordings received:', recordingsRes.data.data?.length || 0, 'recordings');
        setRecordings(recordingsRes.data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch available data:', err);
    }
  }, [customerFilter.isFiltered, customerFilter.customerId, member?.memberType, member?.customerID, isLoginAsCustomer]);

  // Fetch group members
  const fetchGroupMembers = useCallback(async (groupId: number) => {
    try {
      const params: any = {};
      
      if (isLoginAsCustomer && member?.customerID) {
        params.customerId = member.customerID;
      } else if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
        params.customerId = customerFilter.customerId;
      }
      
      const response = await api.get(`/endpoints/ring_groups_v2/${groupId}/members`, { params });
      if (response.data.success) {
        const members = response.data.data || [];
        setMembersMap(prev => new Map(prev).set(groupId, members));
        return members;
      }
      return [];
    } catch (err) {
      console.error('Failed to fetch group members:', err);
      return [];
    }
  }, [customerFilter.isFiltered, customerFilter.customerId, member?.memberType, member?.customerID, isLoginAsCustomer]);

  // Fetch overflow targets based on action
  const fetchOverflowTargets = useCallback(async (appId: string, autoSelectFirst: boolean = false) => {
    // Clear targets first
    setOverflowTargets([]);

    if (!appId || appId === '9') {
      return;
    }

    // SAFETY CHECK: Don't fetch if waiting for login-as data
    if (waitingForLoginAs) {
      console.log('[RingGroupsV2] Still waiting for login-as data, aborting overflow targets fetch');
      return;
    }

    setLoadingTargets(true);
    try {
      console.log('[RingGroupsV2] Fetching targets for appId:', appId);

      const params: any = {};
      if (isLoginAsCustomer && member?.customerID) {
        params.customerId = member.customerID;
      } else if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
        params.customerId = customerFilter.customerId;
      }

      // Use the same endpoint as IVR component - the dedicated overflow-targets endpoint
      const response = await api.get(`/endpoints/ring_groups_v2/overflow-targets/${appId}`, { params });
      console.log('[RingGroupsV2] Overflow targets response:', response.data);

      if (response.data.success) {
        const targets = response.data.data || [];
        setOverflowTargets(targets);

        // Auto-select first target if requested and targets are available
        if (autoSelectFirst && targets.length > 0) {
          setFormData(prev => ({ ...prev, overflow_target: String(targets[0].value) }));
        }
      }
    } catch (err) {
      console.error('Failed to fetch overflow targets:', err);
      setOverflowTargets([]);
    } finally {
      setLoadingTargets(false);
    }
  }, [customerFilter.isFiltered, customerFilter.customerId, member?.memberType, member?.customerID, isLoginAsCustomer, waitingForLoginAs]);

  // Initialize - only run once on mount and when customer filter changes
  useEffect(() => {
    if (!member) return;

    // Check for login token
    const urlParams = new URLSearchParams(window.location.search);
    const hasLoginToken = urlParams.has('lat');
    const sessionData = sessionStorage.getItem('loginAsCustomer');

    // If we have a login token but no session data, wait
    if (hasLoginToken && !sessionData) {
      console.log('[RingGroupsV2] Have login token but no session data - waiting...');
      return;
    }

    // If session data exists, verify it matches member
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        if (parsed.customerId && parsed.customerId !== member.customerID) {
          console.log('[RingGroupsV2] Session/member mismatch, waiting for sync...');
          return;
        }
      } catch (e) {
        console.error('[RingGroupsV2] Error parsing session data:', e);
      }
    }

    // Add small delay to let state settle
    const timer = setTimeout(() => {
      fetchRingGroups();
      fetchAvailableData();
    }, 200);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerFilter.customerId, customerFilter.isFiltered, member?.memberType, member?.customerID, isLoginAsCustomer]);

  // Load overflow targets when overflow action changes
  useEffect(() => {
    if (formData.overflow_action && formData.overflow_action !== 9) {
      fetchOverflowTargets(formData.overflow_action.toString());
    }
  }, [formData.overflow_action, fetchOverflowTargets]);

  // Listen for login-as-customer changes to update tab title and refetch data
  useEffect(() => {
    const handleLoginAsChange = (event: CustomEvent) => {
      if (event.detail && event.detail.customerId && event.detail.customerName) {
        document.title = `${event.detail.customerId} - ${event.detail.customerName} - Ring Groups`;
        // Also refetch data when login-as changes
        setTimeout(() => {
          if (member) {
            fetchRingGroups();
            fetchAvailableData();
          }
        }, 100);
      }
    };

    window.addEventListener('loginAsCustomerChanged', handleLoginAsChange as EventListener);

    // Set initial title if in login-as mode
    if (isLoginAsCustomer && member) {
      const customerId = (member as any).customerID || (member as any).customer_id;
      const customerName = (member as any).loginAsCustomerName || '';
      if (customerId && customerName) {
        document.title = `${customerId} - ${customerName} - Ring Groups`;
      }
    }

    return () => {
      window.removeEventListener('loginAsCustomerChanged', handleLoginAsChange as EventListener);
      // Reset title when component unmounts
      if (!isLoginAsCustomer) {
        document.title = 'Portal';
      }
    };
  }, [isLoginAsCustomer, member]);

  // Handle add/edit group
  const handleSaveGroup = async () => {
    if (!formData.group_number || !formData.group_name) {
      setError('Group number and name are required');
      return;
    }

    // Validate overflow action/target pair
    const validation = validateActionTarget(formData.overflow_action, formData.overflow_target);
    if (!validation.isValid) {
      setError(validation.error || 'Please select a target for the "If No Answer" action');
      return;
    }

    try {
      const endpoint = selectedGroup 
        ? `/endpoints/ring_groups_v2/${selectedGroup.id}`
        : '/endpoints/ring_groups_v2';
      
      const method = selectedGroup ? 'put' : 'post';
      
      // Add customer filter params if needed
      // Map frontend fields to backend fields
      const data = {
        group_number: formData.group_number,
        group_name: formData.group_name,
        ring_strategy: formData.ring_strategy,
        ring_time: formData.ring_time,
        ring_time_per_member: formData.ring_time_per_member,
        skip_busy: formData.skip_busy,
        expand_cell: formData.expand_cell,
        expand_followme: formData.expand_followme,
        expand_sequential: formData.expand_sequential,
        recordID: formData.announcement_id || null,
        max_wait_time: formData.max_wait_time,
        overflow_action: formData.overflow_action,
        overflow_target: formData.overflow_target,
      };
      
      const params: any = {};
      
      if (isLoginAsCustomer && member?.customerID) {
        params.customerId = member.customerID;
      } else if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
        params.customerId = customerFilter.customerId;
      }
      
      console.log('Saving ring group:', { endpoint, method, data, params });
      
      const response = await api[method](endpoint, data, { params });
      
      console.log('Save response:', response);
      
      // Always close dialog and refresh on any successful response
      setSuccess(response.data?.message || 'Ring group saved successfully');
      setDialogOpen(false);
      setSelectedGroup(null);
      // Force refresh with small delay to ensure backend processing is complete
      setTimeout(() => {
        fetchRingGroups();
      }, 100);
      
    } catch (err: any) {
      console.error('Save error:', err);
      setError(err.response?.data?.message || 'Failed to save ring group');
    }
  };

  // Handle delete group
  const handleDeleteConfirm = async () => {
    if (!selectedGroup) return;

    try {
      const params: any = {};
      
      if (isLoginAsCustomer && member?.customerID) {
        params.customerId = member.customerID;
      } else if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
        params.customerId = customerFilter.customerId;
      }
      
      console.log('Deleting ring group:', selectedGroup.id, params);
      
      const response = await api.delete(`/endpoints/ring_groups_v2/${selectedGroup.id}`, { params });
      
      console.log('Delete response:', response);
      
      setSuccess('Ring group deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedGroup(null);
      // Refresh the list
      setTimeout(() => {
        fetchRingGroups();
      }, 100);
      
    } catch (err: any) {
      console.error('Delete error:', err);
      setError(err.response?.data?.message || 'Failed to delete ring group');
      setDeleteDialogOpen(false);
    }
  };

  // Handle view members
  const handleViewMembers = async (group: RingGroup) => {
    setSelectedGroup(group);
    setMemberActionLoading(true);
    
    try {
      await fetchGroupMembers(group.id);
      setMembersDialogOpen(true);
    } finally {
      setMemberActionLoading(false);
    }
  };

  // Add member with optimistic update
  const handleAddMember = async (memberValue: string, type: 'extension' | 'external') => {
    if (!selectedGroup || memberActionLoading) return;

    setMemberActionLoading(true);
    
    // Optimistic update
    const tempId = Date.now();
    const newMember: GroupMember = {
      id: tempId,
      member_value: memberValue,
      member_type: type,
      priority: currentGroupMembers.length,
      ring_timeout: null,
      active: 1,
      display_name: memberValue,
    };

    // Update UI immediately
    setMembersMap(prev => {
      const map = new Map(prev);
      const current = map.get(selectedGroup.id) || [];
      map.set(selectedGroup.id, [...current, newMember]);
      return map;
    });

    try {
      const params: any = {};
      
      if (isLoginAsCustomer && member?.customerID) {
        params.customerId = member.customerID;
      } else if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
        params.customerId = customerFilter.customerId;
      }
      
      const response = await api.post(`/endpoints/ring_groups_v2/${selectedGroup.id}/members`, {
        member_value: memberValue,
        member_type: type,
      }, { params });

      if (response.data.success) {
        // Replace temp member with real one
        const realMembers = await fetchGroupMembers(selectedGroup.id);
        
        if (type === 'external') {
          setExternalNumber('');
        }
        
        // Update ring groups to reflect new member count
        setRingGroups(prev => prev.map(group => 
          group.id === selectedGroup.id 
            ? { ...group, member_count: realMembers.length }
            : group
        ));
      }
    } catch (err: any) {
      // Revert optimistic update on error
      setMembersMap(prev => {
        const map = new Map(prev);
        const current = map.get(selectedGroup.id) || [];
        map.set(selectedGroup.id, current.filter(m => m.id !== tempId));
        return map;
      });
      
      setError(err.response?.data?.message || 'Failed to add member');
    } finally {
      setMemberActionLoading(false);
    }
  };

  // Remove member with optimistic update
  const handleRemoveMember = async (memberId: number) => {
    if (!selectedGroup || memberActionLoading) return;

    setMemberActionLoading(true);
    
    // Store the member for potential restore
    const memberToRemove = currentGroupMembers.find(m => m.id === memberId);
    
    // Optimistic update
    setMembersMap(prev => {
      const map = new Map(prev);
      const current = map.get(selectedGroup.id) || [];
      map.set(selectedGroup.id, current.filter(m => m.id !== memberId));
      return map;
    });

    try {
      const response = await api.delete(
        `/endpoints/ring_groups_v2/${selectedGroup.id}/members/${memberId}`
      );

      if (response.data.success) {
        // Fetch fresh data to ensure consistency
        const updatedMembers = await fetchGroupMembers(selectedGroup.id);
        
        // Update ring groups to reflect new member count
        setRingGroups(prev => prev.map(group => 
          group.id === selectedGroup.id 
            ? { ...group, member_count: updatedMembers.length }
            : group
        ));
      }
    } catch (err: any) {
      // Restore member on error
      if (memberToRemove) {
        setMembersMap(prev => {
          const map = new Map(prev);
          const current = map.get(selectedGroup.id) || [];
          map.set(selectedGroup.id, [...current, memberToRemove]);
          return map;
        });
      }
      
      setError(err.response?.data?.message || 'Failed to remove member');
    } finally {
      setMemberActionLoading(false);
    }
  };

  // Check if extension is selected
  const isExtensionSelected = (extension: string) => {
    return currentGroupMembers.some(
      member => member.member_type === 'extension' && String(member.member_value) === String(extension)
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Ring Groups</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => {
            setSelectedGroup(null);
            // Set next available group number
            const existingNumbers = ringGroups
              .map(g => parseInt(g.group_number))
              .filter(n => n >= 8000 && n <= 8999)
              .sort((a, b) => a - b);
            
            let nextNumber = '8000';
            for (let i = 8000; i <= 8999; i++) {
              if (!existingNumbers.includes(i)) {
                nextNumber = i.toString();
                break;
              }
            }
            
            setFormData({
              group_number: nextNumber,
              group_name: '',
              ring_strategy: 'ringall',
              ring_time: 18,
              ring_time_per_member: 12,
              skip_busy: true,
              expand_cell: true,
              expand_followme: false,
              expand_sequential: false,
              announcement_id: '',
              max_wait_time: 180,
              overflow_action: 7,
              overflow_target: '1000',
            });
            setDialogOpen(true);
          }}
        >
          Add Ring Group
        </Button>
      </Box>

      {isMobile ? (
        // Mobile view - Cards
        <Box display="flex" flexWrap="wrap" gap={2}>
          {ringGroups.map((group) => (
            <Card key={group.id} sx={{ width: '100%', maxWidth: 400 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                  <Typography variant="h6" component="h2">
                    {group.group_number}
                  </Typography>
                  <Box>
                    {group.announcement_id && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          // Play announcement logic would go here
                          console.log('Play announcement for group:', group.id);
                        }}
                        title="Play Announcement"
                      >
                        <PlayIcon />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => handleViewMembers(group)}
                      title="Manage Members"
                    >
                      <GroupIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedGroup(group);
                        setFormData({
                          group_number: group.group_number,
                          group_name: group.group_name,
                          ring_strategy: group.ring_strategy,
                          ring_time: group.ring_time,
                          ring_time_per_member: group.ring_time_per_member,
                          skip_busy: group.skip_busy,
                          expand_cell: group.expand_cell,
                          expand_followme: group.expand_followme,
                          expand_sequential: group.expand_sequential,
                          announcement_id: group.announcement_id?.toString() || '',
                          max_wait_time: group.max_wait_time,
                          overflow_action: group.overflow_action || 7,
                          overflow_target: group.overflow_target || '',
                        });
                        setDialogOpen(true);
                        // Load overflow targets if there's an overflow action
                        if (group.overflow_action && group.overflow_action !== 9) {
                          console.log('[RingGroupsV2] Loading targets for overflow_action:', group.overflow_action);
                          setTimeout(() => {
                            fetchOverflowTargets(group.overflow_action!.toString());
                          }, 100);
                        }
                      }}
                      title="Edit"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedGroup(group);
                        setDeleteDialogOpen(true);
                      }}
                      title="Delete"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {group.group_name.replace(/^\d+-/, '')}
                </Typography>
                
                <Box display="flex" flexDirection="column" gap={0.5} mt={2}>
                  <Typography variant="caption" color="text.secondary">
                    Strategy: {group.ring_strategy}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Ring Time: {Math.round(group.ring_time / 6)} rings
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Members: <Chip label={group.member_count} size="small" />
                  </Typography>
                  {group.overflow_action_name && (
                    <Typography variant="caption" color="text.secondary">
                      Overflow: {group.overflow_action_name}
                      {group.overflow_target_desc && ` → ${group.overflow_target_desc}`}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        // Desktop view - Table
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Group Number</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Strategy</TableCell>
                <TableCell align="center">Members</TableCell>
                <TableCell>Ring Time</TableCell>
                <TableCell>Overflow</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ringGroups.map((group) => (
                <TableRow key={group.id} hover>
                  <TableCell>
                    <Typography variant="subtitle2">{group.group_number}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {group.group_name.replace(/^\d+-/, '')}
                    </Typography>
                  </TableCell>
                  <TableCell>{group.ring_strategy}</TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={group.member_count} 
                      size="small" 
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{Math.round(group.ring_time / 6)} rings</TableCell>
                  <TableCell>
                    {group.overflow_action_name ? (
                      <Box>
                        <Typography variant="caption">
                          {group.overflow_action_name}
                        </Typography>
                        {group.overflow_target_desc && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            → {group.overflow_target_desc}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        None
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {group.announcement_id && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          // Play announcement logic would go here
                          console.log('Play announcement for group:', group.id);
                        }}
                        title="Play Announcement"
                      >
                        <PlayIcon />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => handleViewMembers(group)}
                      title="Manage Members"
                      color="primary"
                    >
                      <GroupIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedGroup(group);
                        setFormData({
                          group_number: group.group_number,
                          group_name: group.group_name,
                          ring_strategy: group.ring_strategy,
                          ring_time: group.ring_time,
                          ring_time_per_member: group.ring_time_per_member,
                          skip_busy: group.skip_busy,
                          expand_cell: group.expand_cell,
                          expand_followme: group.expand_followme,
                          expand_sequential: group.expand_sequential,
                          announcement_id: group.announcement_id?.toString() || '',
                          max_wait_time: group.max_wait_time,
                          overflow_action: group.overflow_action || 7,
                          overflow_target: group.overflow_target || '',
                        });
                        setDialogOpen(true);
                        // Load overflow targets if there's an overflow action
                        if (group.overflow_action && group.overflow_action !== 9) {
                          console.log('[RingGroupsV2] Loading targets for overflow_action:', group.overflow_action);
                          setTimeout(() => {
                            fetchOverflowTargets(group.overflow_action!.toString());
                          }, 100);
                        }
                      }}
                      title="Edit"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedGroup(group);
                        setDeleteDialogOpen(true);
                      }}
                      title="Delete"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {ringGroups.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No ring groups found. Click "Add Ring Group" to create one.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {ringGroups.length === 0 && isMobile && (
        <Box width="100%" textAlign="center" py={4}>
          <Typography color="text.secondary">
            No ring groups found. Click "Add Ring Group" to create one.
          </Typography>
        </Box>
      )}

      {/* Members Dialog */}
      <Dialog 
        open={membersDialogOpen} 
        onClose={() => setMembersDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          {selectedGroup && `Members - ${selectedGroup.group_name} (${selectedGroup.group_number})`}
        </DialogTitle>
        <DialogContent>
          {/* Add External Number */}
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Add External Number"
              value={externalNumber}
              onChange={(e) => setExternalNumber(e.target.value)}
              placeholder="10 digit number"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      size="small"
                      onClick={() => {
                        if (externalNumber.length === 10) {
                          handleAddMember(externalNumber, 'external');
                        }
                      }}
                      disabled={externalNumber.length !== 10 || memberActionLoading}
                    >
                      Add
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Member List */}
          <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Select Extensions
            </Typography>
            <List dense>
              {/* External Members */}
              {currentGroupMembers
                .filter(member => member.member_type === 'external')
                .map((member) => (
                  <ListItem key={`external-${member.id}`}>
                    <Checkbox
                      checked={true}
                      onChange={() => handleRemoveMember(member.id)}
                      disabled={memberActionLoading}
                      size="small"
                    />
                    <ListItemText
                      primary={member.member_value}
                      secondary="External Number"
                    />
                  </ListItem>
                ))}
              
              {/* Extension Members */}
              {availableExtensions.map((ext) => {
                const isSelected = isExtensionSelected(ext.extension);
                const member = currentGroupMembers.find(
                  m => m.member_type === 'extension' && String(m.member_value) === String(ext.extension)
                );
                
                return (
                  <ListItem key={`extension-${ext.extension}`}>
                    <Checkbox
                      checked={isSelected}
                      onChange={() => {
                        if (isSelected && member) {
                          handleRemoveMember(member.id);
                        } else {
                          handleAddMember(ext.extension, 'extension');
                        }
                      }}
                      disabled={memberActionLoading}
                      size="small"
                    />
                    <ListItemText
                      primary={`${ext.extension} - ${ext.name}`}
                      secondary={ext.email}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setMembersDialogOpen(false);
              // Refresh ring groups to update member counts
              fetchRingGroups();
            }}
            disabled={memberActionLoading}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit/Create Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          {selectedGroup ? `Edit Ring Group ${selectedGroup.group_number}` : 'Add New Ring Group'}
        </DialogTitle>
        {error && (
          <Box sx={{ px: 3, pt: 1 }}>
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          </Box>
        )}
        <DialogContent sx={{ pb: 2 }}>
          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            {/* Row 1: Basic Info */}
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Group Number"
                value={formData.group_number}
                onChange={(e) => setFormData({ ...formData, group_number: e.target.value })}
                disabled={!!selectedGroup}
                placeholder="8000-8999"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Group Name"
                value={formData.group_name}
                onChange={(e) => setFormData({ ...formData, group_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <FormControl fullWidth size="small">
                <InputLabel>Ring Strategy</InputLabel>
                <Select
                  value={formData.ring_strategy}
                  onChange={(e) => setFormData({ ...formData, ring_strategy: e.target.value as any })}
                  label="Ring Strategy"
                >
                  <MenuItem value="ringall">Ring All</MenuItem>
                  <MenuItem value="sequential">Sequential</MenuItem>
                  <MenuItem value="random">Random</MenuItem>
                  <MenuItem value="roundrobin">Round Robin</MenuItem>
                  <MenuItem value="leastrecent">Least Recent</MenuItem>
                  <MenuItem value="fewestcalls">Fewest Calls</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Strategy explanation */}
            <Grid item xs={12}>
              <Alert severity="info" sx={{ py: 0.5, fontSize: '0.875rem' }}>
                {formData.ring_strategy === 'ringall' && 'All members ring simultaneously. First to answer gets the call.'}
                {formData.ring_strategy === 'sequential' && 'Members ring one at a time in order. Each rings for specified time before next.'}
                {formData.ring_strategy === 'random' && 'Members ring in random order for unpredictable distribution.'}
                {formData.ring_strategy === 'roundrobin' && 'Starts with next member after last answered call.'}
                {formData.ring_strategy === 'leastrecent' && 'Rings member who hasn\'t received a call longest.'}
                {formData.ring_strategy === 'fewestcalls' && 'Rings member with fewest calls today.'}
              </Alert>
            </Grid>

            {/* Row 2: Ring Settings */}
            {formData.ring_strategy === 'ringall' ? (
              <>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Ring Time</InputLabel>
                    <Select
                      value={Math.round(formData.ring_time / 6)}
                      onChange={(e) => setFormData({ ...formData, ring_time: parseInt(e.target.value as string) * 6 })}
                      label="Ring Time"
                    >
                      {[2, 3, 4, 5, 6, 7, 8, 9, 10].map((rings) => (
                        <MenuItem key={rings} value={rings}>
                          {rings} rings
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={formData.skip_busy}
                        onChange={(e) => setFormData({ ...formData, skip_busy: e.target.checked })}
                      />
                    }
                    label="Skip Busy"
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Per Member</InputLabel>
                    <Select
                      value={Math.round(formData.ring_time_per_member / 6)}
                      onChange={(e) => setFormData({ ...formData, ring_time_per_member: parseInt(e.target.value as string) * 6 })}
                      label="Per Member"
                    >
                      {[2, 3, 4, 5, 6, 7, 8, 9, 10].map((rings) => (
                        <MenuItem key={rings} value={rings}>
                          {rings} rings
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Timeout</InputLabel>
                    <Select
                      value={[1, 2, 3, 5, 10, 15, 30, 60].includes(formData.max_wait_time / 60) ? formData.max_wait_time / 60 : 0}
                      onChange={(e) => {
                        const value = parseInt(e.target.value as string);
                        if (value === 0) {
                          // Custom timeout - would need to add state for this
                        } else {
                          setFormData({ ...formData, max_wait_time: value * 60 });
                        }
                      }}
                      label="Timeout"
                    >
                      <MenuItem value={1}>1 minute</MenuItem>
                      <MenuItem value={2}>2 minutes</MenuItem>
                      <MenuItem value={3}>3 minutes</MenuItem>
                      <MenuItem value={5}>5 minutes</MenuItem>
                      <MenuItem value={10}>10 minutes</MenuItem>
                      <MenuItem value={15}>15 minutes</MenuItem>
                      <MenuItem value={30}>30 minutes</MenuItem>
                      <MenuItem value={60}>60 minutes</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={formData.skip_busy}
                        onChange={(e) => setFormData({ ...formData, skip_busy: e.target.checked })}
                      />
                    }
                    label="Skip Busy"
                  />
                </Grid>
              </>
            )}

            {/* Member Expansion Options */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Member Expansion Options
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={formData.expand_cell}
                    onChange={(e) => setFormData({ ...formData, expand_cell: e.target.checked })}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">Include member cell phones</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Ring members' cell phones along with their extension
                    </Typography>
                  </Box>
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={formData.expand_followme}
                    onChange={(e) => setFormData({ ...formData, expand_followme: e.target.checked })}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">Include Follow-Me destinations</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Ring members' Follow-Me destinations simultaneously
                    </Typography>
                  </Box>
                }
              />
            </Grid>
            {formData.expand_followme && (
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={formData.expand_sequential}
                      onChange={(e) => setFormData({ ...formData, expand_sequential: e.target.checked })}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">Include sequential Follow-Me</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Use multi-attempt dialing for sequential destinations
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
            )}

            {/* Row 3: Optional Features */}
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Recording (Optional)</InputLabel>
                <Select
                  value={formData.announcement_id}
                  onChange={(e) => setFormData({ ...formData, announcement_id: e.target.value })}
                  label="Recording (Optional)"
                >
                  <MenuItem value="">None</MenuItem>
                  {recordings.map((recording) => (
                    <MenuItem key={recording.RecordID} value={recording.RecordID}>
                      {recording.Name}{recording.Notes ? ` - ${recording.Notes}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Row 4: If Unanswered */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>If No Answer</InputLabel>
                <Select
                  value={formData.overflow_action}
                  onChange={(e) => {
                    const appId = Number(e.target.value);
                    setFormData({ ...formData, overflow_action: appId, overflow_target: '' });
                    fetchOverflowTargets(appId.toString(), true);
                  }}
                  label="If No Answer"
                >
                  <MenuItem value={9}>End Call</MenuItem>
                  {overflowActions.map((action) => (
                    <MenuItem key={action.app_id} value={action.app_id}>
                      {action.app_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              {formData.overflow_action !== 9 && formData.overflow_action && (loadingTargets || overflowTargets.length > 0) ? (
                <FormControl fullWidth size="small" disabled={loadingTargets}>
                  <InputLabel>Send To</InputLabel>
                  <Select
                    value={formData.overflow_target || ''}
                    onChange={(e) => setFormData({ ...formData, overflow_target: e.target.value })}
                    label="Send To"
                  >
                    {loadingTargets ? (
                      <MenuItem disabled>Loading...</MenuItem>
                    ) : (
                      overflowTargets.map((target) => (
                        <MenuItem key={target.value} value={String(target.value)}>
                          {target.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              ) : null}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => {
            setDialogOpen(false);
            setSelectedGroup(null);
          }}>Cancel</Button>
          <Button onClick={handleSaveGroup} variant="contained">
            {selectedGroup ? 'Update' : 'Create'} Ring Group
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => {
        setDeleteDialogOpen(false);
        setSelectedGroup(null);
      }}>
        <DialogTitle>Delete Ring Group</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete ring group {selectedGroup?.group_number} ({selectedGroup?.group_name})?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteDialogOpen(false);
            setSelectedGroup(null);
          }}>Cancel</Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={!selectedGroup}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Notification */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RingGroupsV2;